import type { ReactNode } from 'react';
import { cn } from '@/utils/classnames';

export interface JsonViewProps {
  value: unknown;
  /** Visual variant — "deep" matches the dark ID Token preview panel. */
  variant?: 'panel' | 'deep';
  className?: string;
}

const VARIANT_BG: Record<NonNullable<JsonViewProps['variant']>, string> = {
  panel: 'bg-[#1E1E1E]',
  deep: 'bg-[#14233D]',
};

const COLORS = {
  key: 'text-[#9CDCFE]',
  string: 'text-[#CE9178]',
  number: 'text-[#B5CEA8]',
  boolean: 'text-[#C586C0]',
  nullish: 'text-[#9CA3AF]',
  punct: 'text-[#D4D4D4]',
} as const;

function renderValue(value: unknown, depth: number): ReactNode {
  if (value === null) return <span className={COLORS.nullish}>null</span>;
  if (value === undefined) return <span className={COLORS.nullish}>undefined</span>;
  if (typeof value === 'string') return <span className={COLORS.string}>"{value}"</span>;
  if (typeof value === 'number') return <span className={COLORS.number}>{String(value)}</span>;
  if (typeof value === 'boolean') return <span className={COLORS.boolean}>{String(value)}</span>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className={COLORS.punct}>[]</span>;
    const indent = '  '.repeat(depth + 1);
    const closing = '  '.repeat(depth);
    return (
      <>
        <span className={COLORS.punct}>[</span>
        {value.map((item, i) => (
          <div key={i}>
            <span className={COLORS.punct}>{indent}</span>
            {renderValue(item, depth + 1)}
            {i < value.length - 1 ? <span className={COLORS.punct}>,</span> : null}
          </div>
        ))}
        <div>
          <span className={COLORS.punct}>{closing}]</span>
        </div>
      </>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className={COLORS.punct}>{'{}'}</span>;
    const indent = '  '.repeat(depth + 1);
    const closing = '  '.repeat(depth);
    return (
      <>
        <span className={COLORS.punct}>{'{'}</span>
        {entries.map(([k, v], i) => (
          <div key={k}>
            <span className={COLORS.punct}>{indent}</span>
            <span className={COLORS.key}>"{k}"</span>
            <span className={COLORS.punct}>: </span>
            {renderValue(v, depth + 1)}
            {i < entries.length - 1 ? <span className={COLORS.punct}>,</span> : null}
          </div>
        ))}
        <div>
          <span className={COLORS.punct}>{closing}{'}'}</span>
        </div>
      </>
    );
  }
  return <span className={COLORS.nullish}>{String(value)}</span>;
}

export function JsonView({ value, variant = 'panel', className }: JsonViewProps) {
  return (
    <pre
      className={cn(
        'overflow-x-auto rounded-lg px-container-padding py-stack-md',
        'font-code-md text-code-md text-[#E5E7EB]',
        VARIANT_BG[variant],
        className,
      )}
    >
      <code>{renderValue(value, 0)}</code>
    </pre>
  );
}
